/*
 * assets/global.js
 * -----------------------------------------------------------------
 * Fragarabic — versión unificada y corregida para menú móvil y componentes.
 * -----------------------------------------------------------------
 */

(function () {
  'use strict';

  /* ============================================================
     1. UTILIDADES COMPARTIDAS
  ============================================================ */

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  }

  const trapFocusHandlers = {};

  function trapFocus(container, elementToFocus = container) {
    const elements = getFocusableElements(container);
    if (!elements.length) return;
    const first = elements[0];
    const last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (event.target !== container && event.target !== last && event.target !== first) return;
      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = function () {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = function (event) {
      if (event.code?.toUpperCase() !== 'TAB') return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    (elementToFocus || container).focus();
  }

  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
    if (elementToFocus) elementToFocus.focus();
  }

  window.FragarabicUtils = window.FragarabicUtils || {
    debounce,
    trapFocus,
    removeTrapFocus,
    getFocusableElements,
  };

  /* ============================================================
     2. COMPONENTES NATIVOS DE DAWN — MENÚ / DRAWER / MODAL
  ============================================================ */

  if (!customElements.get('details-disclosure')) {
    class DetailsDisclosure extends HTMLElement {
      constructor() {
        super();
        this.mainDetailsToggle = this.querySelector('details');
        if (!this.mainDetailsToggle) return;
        this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

        this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
        this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
      }

      onFocusOut() {
        setTimeout(() => {
          if (!this.mainDetailsToggle.contains(document.activeElement)) this.close();
        });
      }

      onToggle() {
        if (!this.content) return;
        if (!this.animations) this.animations = this.content.getAnimations();
        if (this.mainDetailsToggle.hasAttribute('open')) {
          this.animations.forEach((a) => a.play());
        }
      }

      close() {
        this.mainDetailsToggle.removeAttribute('open');
        this.mainDetailsToggle.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }
    }
    customElements.define('details-disclosure', DetailsDisclosure);
  }

  if (!customElements.get('details-modal')) {
    class DetailsModal extends HTMLElement {
      constructor() {
        super();
        this.detailsContainer = this.querySelector('details');
        this.summaryToggle = this.querySelector('summary');
        if (!this.detailsContainer || !this.summaryToggle) return;

        this.detailsContainer.addEventListener('keyup', (event) => {
          if (event.code?.toUpperCase() === 'ESCAPE') this.close();
        });
        this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
        this.querySelector('button[type="button"]')?.addEventListener('click', this.close.bind(this));

        this.summaryToggle.setAttribute('role', 'button');
        this.summaryToggle.setAttribute('aria-expanded', 'false');
      }

      isOpen() {
        return this.detailsContainer.hasAttribute('open');
      }

      onSummaryClick(event) {
        event.preventDefault();
        event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
      }

      onBodyClick(event) {
        if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
      }

      open(event) {
        this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
        event.target.closest('details').setAttribute('open', true);
        document.body.addEventListener('click', this.onBodyClickEvent);
        document.body.classList.add('overflow-hidden');
        this.summaryToggle.setAttribute('aria-expanded', 'true');
        trapFocus(this.detailsContainer);
      }

      close(focusToggle = true) {
        removeTrapFocus(focusToggle ? this.summaryToggle : null);
        this.detailsContainer.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClickEvent);
        document.body.classList.remove('overflow-hidden');
        this.summaryToggle.setAttribute('aria-expanded', 'false');
      }
    }
    customElements.define('details-modal', DetailsModal);
  }

  // Clase unificada y robusta para MenuDrawer (compatible con <component-menu-drawer>)
  class MenuDrawer extends HTMLElement {
    constructor() {
      super();
      this.mainDetailsToggle = this.querySelector('details');
      if (!this.mainDetailsToggle) return;
      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.bindEvents();
    }

    bindEvents() {
      this.querySelectorAll('summary').forEach((summary) =>
        summary.addEventListener('click', this.onSummaryClick.bind(this))
      );
      this.querySelectorAll('button[data-close-drawer], .menu-drawer__close-button').forEach((button) =>
        button.addEventListener('click', this.onCloseButtonClick.bind(this))
      );
      
      // Control para el overlay de fondo
      const screen = this.querySelector('.menu-drawer__screen');
      if (screen) {
        screen.addEventListener('click', () => this.closeMenuDrawer());
      }
    }

    onKeyUp(event) {
      if (event.code?.toUpperCase() !== 'ESCAPE') return;
      const openDetails = event.target.closest('details[open]');
      if (!openDetails) return;
      openDetails === this.mainDetailsToggle
        ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
        : this.closeSubmenu(openDetails);
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.closest('details');
      const isOpen = detailsElement.hasAttribute('open');

      if (detailsElement === this.mainDetailsToggle) {
        if (isOpen) event.preventDefault();
        isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
      } else {
        setTimeout(() => detailsElement.classList.add('menu-opening'));
      }
    }

    openMenuDrawer(summaryElement) {
      this.mainDetailsToggle.setAttribute('open', true);
      this.setAttribute('open', '');
      summaryElement.setAttribute('aria-expanded', 'true');
      trapFocus(this.mainDetailsToggle, summaryElement);
      document.body.classList.add('overflow-hidden');
    }

    closeMenuDrawer(event, elementToFocus = false) {
      if (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      this.mainDetailsToggle.removeAttribute('open');
      this.removeAttribute('open');
      const summary = this.mainDetailsToggle.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(elementToFocus);
    }

    onFocusOut() {
      setTimeout(() => {
        if (
          this.mainDetailsToggle.hasAttribute('open') &&
          !this.mainDetailsToggle.contains(document.activeElement)
        ) {
          this.closeMenuDrawer();
        }
      });
    }

    onCloseButtonClick(event) {
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      detailsElement.removeAttribute('open');
    }
  }

  if (!customElements.get('menu-drawer')) {
    customElements.define('menu-drawer', MenuDrawer);
  }

  // Soporte directo para <component-menu-drawer> registrado con la misma lógica
  if (!customElements.get('component-menu-drawer')) {
    customElements.define('component-menu-drawer', class ComponentMenuDrawer extends MenuDrawer {});
  }

  if (!customElements.get('header-drawer')) {
    const BaseMenuDrawer = customElements.get('menu-drawer');
    class HeaderDrawer extends BaseMenuDrawer {
      openMenuDrawer(summaryElement) {
        this.header = this.header || document.querySelector('.section-header, header');
        if (this.header) {
          this.header.classList.add('menu-open');
          document.documentElement.style.setProperty(
            '--header-bottom-position',
            `${Math.round(this.header.getBoundingClientRect().bottom)}px`
          );
        }
        super.openMenuDrawer(summaryElement);
      }

      closeMenuDrawer(event, elementToFocus) {
        super.closeMenuDrawer(event, elementToFocus);
        this.header?.classList.remove('menu-open');
      }
    }
    customElements.define('header-drawer', HeaderDrawer);
  }

  /* ============================================================
     3. QUANTITY INPUT
  ============================================================ */

  if (!customElements.get('quantity-input')) {
    class QuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        if (!this.input) return;
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
          button.addEventListener('click', this.onButtonClick.bind(this))
        );
      }

      setMax(max) {
        if (max === null || max === undefined || max === '') {
          this.input.removeAttribute('max');
        } else {
          this.input.setAttribute('max', max);
        }
        this.validateQtyRules();
      }

      onInputChange() {
        this.validateQtyRules();
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;
        event.currentTarget.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
      }

      validateQtyRules() {
        const value = parseInt(this.input.value, 10);
        if (Number.isNaN(value)) return;

        if (this.input.min !== '') {
          const min = parseInt(this.input.min, 10);
          const buttonMinus = this.querySelector("button[name='minus']");
          if (buttonMinus) buttonMinus.classList.toggle('disabled', value <= min);
          if (value < min) this.input.value = min;
        }
        if (this.input.max !== '') {
          const max = parseInt(this.input.max, 10);
          const buttonPlus = this.querySelector("button[name='plus']");
          if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= max);
          if (value > max) this.input.value = max;
        }
      }
    }
    customElements.define('quantity-input', QuantityInput);
  }

  /* ============================================================
     4. VARIANT SELECTS / VARIANT RADIOS
  ============================================================ */

  if (!customElements.get('variant-selects')) {
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onVariantChange.bind(this));
      }

      onVariantChange() {
        this.updateOptions();
        this.updateMasterId();
        this.updateInventorySync();
        this.updateVariantStatus();
        this.updateURL();
        this.updateVariantInput();
      }

      updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        this.options = fieldsets
          .map((fieldset) => {
            const checked = fieldset.querySelector('input:checked');
            if (checked) return checked.value;
            const select = fieldset.querySelector('select');
            return select ? select.value : undefined;
          })
          .filter((value) => value !== undefined);
      }

      getVariantData() {
        if (!this.variantData) {
          const jsonScript = this.querySelector('script[type="application/json"]');
          this.variantData = jsonScript ? JSON.parse(jsonScript.textContent) : [];
        }
        return this.variantData;
      }

      get currentVariant() {
        const variants = this.getVariantData();
        return variants.find((variant) =>
          variant.options.every((optionValue, index) => optionValue === this.options[index])
        );
      }

      updateMasterId() {
        this.currentVariantId = this.currentVariant ? this.currentVariant.id : null;
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false' || !this.dataset.url) return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateVariantInput() {
        const forms = document.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        );
        forms.forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (!input) return;
          input.value = this.currentVariantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateInventorySync() {
        const variant = this.currentVariant;
        const quantityWrapper = document.querySelector(
          `#Quantity-Form-${this.dataset.section} quantity-input, quantity-input[data-section="${this.dataset.section}"]`
        );
        if (!quantityWrapper) return;

        let max = null;

        if (variant) {
          const trackedByShopify = variant.inventory_management === 'shopify';
          const allowsOversell = variant.inventory_policy === 'continue';
          const stock = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : null;

          if (trackedByShopify && !allowsOversell) {
            max = stock !== null ? Math.max(stock, 0) : null;
          }
        }

        if (typeof quantityWrapper.setMax === 'function') {
          quantityWrapper.setMax(max);
        } else {
          const input = quantityWrapper.querySelector('input');
          if (input) {
            if (max === null) input.removeAttribute('max');
            else input.setAttribute('max', max);
          }
        }
      }

      updateVariantStatus() {
        const addButton = document.querySelector(`#product-form-${this.dataset.section} [name="add"]`);
        if (!addButton) return;
        const addButtonText = addButton.querySelector('span');
        const variant = this.currentVariant;
        const strings = window.variantStrings || {};

        if (!variant) {
          addButton.setAttribute('disabled', 'disabled');
          if (addButtonText) addButtonText.textContent = strings.unavailable || 'No disponible';
          return;
        }

        const soldOut =
          variant.inventory_management === 'shopify' &&
          variant.inventory_policy !== 'continue' &&
          variant.inventory_quantity <= 0;

        if (variant.available === false || soldOut) {
          addButton.setAttribute('disabled', 'disabled');
          if (addButtonText) addButtonText.textContent = strings.soldOut || 'Agotado';
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText) addButtonText.textContent = strings.addToCart || 'Añadir al carrito';
        }
      }
    }

    window.VariantSelects = VariantSelects;
    customElements.define('variant-selects', VariantSelects);
  }

  if (!customElements.get('variant-radios')) {
    const BaseVariantSelects = customElements.get('variant-selects');
    class VariantRadios extends BaseVariantSelects {
      onVariantChange() {
        super.onVariantChange();
        this.updatePillState();
      }

      updatePillState() {
        const variants = this.getVariantData();
        this.querySelectorAll('.variant-cards-container input[type="radio"]').forEach((input) => {
          const matching = variants.find((v) => v.id === Number(input.dataset.variantId));
          const card = input.closest('.variant-pill, label');
          if (!card || !matching) return;
          const unavailable =
            matching.available === false ||
            (matching.inventory_management === 'shopify' &&
              matching.inventory_policy !== 'continue' &&
              matching.inventory_quantity <= 0);
          card.classList.toggle('variant-pill--sold-out', unavailable);
        });
      }
    }
    customElements.define('variant-radios', VariantRadios);
  }
})();